import {AppConfig} from '@frontend/AppConfig'
import {detectSearchPanelLocationOnLoad} from '@frontend/helpers/detectSearchPanelLocationOnLoad'
import {LocalStorage} from '@frontend/services/LocalStorage'
import {
    useAdsStore,
    useAppRootStore,
    useAuthStore,
    useLocaleStore,
    useUiLayoutStore,
} from '@frontend/stores'
import {TrackingService} from '@shared/services/TrackingService'
import {useEffect} from 'react'
import type {Location} from 'react-router-dom'
import {handleErrorResponse} from 'swayok-react-mdb-ui-kit/helpers/api/ApiRequestErrorHelpers'
import type {ApiError} from 'swayok-react-mdb-ui-kit/services/ApiRequestService'
import {NavigationService} from 'swayok-react-mdb-ui-kit/services/NavigationService'
import {ToastService} from 'swayok-react-mdb-ui-kit/services/ToastService'
import {AnalyticsEventReporter} from '../helpers/AnalyticsEventReporter'
import {onAppLoaded} from '../helpers/onAppLoaded'
import {AnalyticsService} from '../services/AnalyticsService'

// Инициализация приложения при монтировании компонента.
export function useAppInitialization(delay: number = 1000) {
    const reloadUserData = useAuthStore(
        state => state.reloadUserData
    )
    const setUserData = useAuthStore(
        state => state.setUserData
    )
    const initLocale = useLocaleStore(
        state => state.init
    )
    const setIsLoading = useAppRootStore(
        state => state.setIsLoading
    )
    const initUiLayout = useUiLayoutStore(
        state => state.init
    )
    const loadCategories = useAdsStore(
        state => state.loadCategories
    )
    const loadLocations = useAdsStore(
        state => state.loadLocations
    )
    const setIsSearchPanelDisabled = useAdsStore(
        state => state.setIsSearchPanelDisabled
    )

    useEffect(() => {
        // Инициализация определения размера экрана.
        initUiLayout()

        const abortController: AbortController = new AbortController()

        // Определение и загрузка языка.
        void initLocale().then(locale => {
            // Отсрочка окончания загрузки (иногда требуется для анимаций).
            setTimeout(() => {
                setIsLoading(false)
            }, delay)
            // Инициализация трекинга (Должна быть ДО AnalyticsService.init()).
            TrackingService.init(
                AppConfig.trackingConfig,
                locale.language.full
            )
            // DeviceInfoTrackerService.init(
            //     AppConfig.trackingConfig.apiUrl,
            //     AppConfig.trackingConfig.siteId
            // )
            AnalyticsService.init(AppConfig.analyticsConfig)
            NavigationService.registerNavigationCallback(
                'AnalyticsService',
                (location: Location) => {
                    AnalyticsService.onUrlChange(
                        NavigationService.makeAbsoluteUrlFormLocation(location)
                    )
                }
            )
            AnalyticsEventReporter.setRegionConfig(locale.region)
            ToastService.configure({
                onErrorToast: AnalyticsEventReporter.errorToastShown,
            })

            void Promise.all([
                // Загрузка списка категорий.
                loadCategories(false, abortController)
                    .catch(handleErrorResponse),
                // Загрузка списка локаций.
                loadLocations(
                    false,
                    (locations, location) => detectSearchPanelLocationOnLoad(
                        locations,
                        location,
                        LocalStorage.getFilterLocation(),
                        locale.region.defaultCity
                    ),
                    abortController
                )
                    .catch(handleErrorResponse),
            ])
                .then(() => {
                    setIsSearchPanelDisabled(false)
                })

            if (AppConfig.isAuthenticated) {
                // Запрос данных авторизованного пользователя (из кеша или загрузить из API).
                reloadUserData(false, true)
                    .catch((error: ApiError) => {
                        if (error.errorType === 'abort') {
                            return
                        }
                        // Для части ошибок не нужно обнулять данные пользователя.
                        // 429 - too many requests, 408 - timeout.
                        if (error.status >= 500 || error.status === 429 || error.status === 408) {
                            handleErrorResponse(error)
                            return
                        }
                        setUserData(null, true)
                        handleErrorResponse(error, {
                            // Отключаем стандартный обработчик ошибок авторизации.
                            unauthorisedError: null,
                        })
                    })
            }
        })

        onAppLoaded()

        window.appLoaded = true

        return () => {
            abortController.abort()
        }
    }, [])
}
